import type { Section, WrittenPage } from '../sections'
import { home } from './home'
import { SOLUTIONS_FORM_EN } from '../solutionsForm'
import { LINKS } from './links'

/**
 * The Solutions hub.
 *
 * Not in Halil's document, which specifies the three solution pages and no parent.
 * It exists because `/solutions/` redirects here (and `/solutions/maturity-frameworks/`
 * did until 2026-10-02) and a 301 into a 404 is worse than either — and because
 * "solutions" is a real query.
 *
 * It reuses the home page's three cards rather than restating them. One
 * description of the three offerings, in one place, is also the honest answer to
 * a hub page: there is nothing here the cards do not already say.
 */
const cards = home.sections.find(
  (s): s is Extract<Section, { kind: 'cards' }> => s.kind === 'cards',
)!

/*
  The home page's customer logo wall, reused here for the same reason as the
  cards: one list of marks, in one place. `check` is dropped on the way through
  so the home page keeps its amber note and this page does not repeat it.
*/
const { check: _logosCheck, ...logoWall } = home.sections.find(
  (s): s is Extract<Section, { kind: 'logos' }> => s.kind === 'logos',
)!

/*
  The home page's two customer quotes, reused for the same reason as the cards
  and the logo wall. `variant: 'minimal'` is the Hall of Fame card pared back —
  the design the Transform Better playbook closes on, in its small form, which
  is what was asked for on 2026-08-31 ("ufak bir şekilde olsun"). `check` is
  dropped on the way through so the note stays on the home page alone.
*/
const { check: _quotesCheck, ...quotes } = home.sections.find(
  (s): s is Extract<Section, { kind: 'quotes' }> => s.kind === 'quotes',
)!

export const solutions: WrittenPage = {
  navLabel: 'Solutions',
  title: 'Solutions',
  description:
    'Strategy Consulting, Transformation Management and Enterprise AI Solutions. Discover, deploy, and get to an outcome.',
  sections: [
    // The hero is the three stages and nothing else — asked for on 2026-08-28
    // with a screenshot of the old eyebrow, headline and sub ("bunlar
    // yazmasın"). The cards below already carry the argument the sub was
    // making, so the page opens on the sequence itself.
    {
      kind: 'hero',
      headline: 'Discover. Deploy. Outcome.',
    },
    // Same three cards as the home page, kickerless and with pill CTAs since
    // 2026-08-28. Here the three titles are links to their own pages as well
    // ("tıklanabilir olsun ancak visually aynı kalsın", 2026-08-28) — the same
    // destination as the Explore button below each one, drawn exactly as the
    // plain heading was. Spread rather than mutated so the home page, which
    // reuses this very object, is unaffected.
    { ...cards, linkTitles: true },
    // The same customer logo wall as the home page — asked for on 2026-08-31
    // with a screenshot of it ("bu kısım solutions sayfasında da olsun"). It
    // sits after the three cards and before the form, so the page reads
    // offering → proof → contact.
    logoWall,
    // The two customer quotes, in the pared-back Hall of Fame card. They sit
    // between the marks and the form, so the proof runs from who works with us
    // to what they say about it.
    quotes,
    /*
      The Transform Better e-book card, in the same design it closes its own
      page with — asked for on 2026-08-31 with a screenshot of that card ("bu
      transform better ebook tasarımı aynı şekli ile solutions sayfasında
      uygun şekilde aşağıda olsun"). Same heading, same fanned-out covers; the
      one difference is where the button goes, which was the other half of the
      request: here it leads to the e-book's own page rather than straight to
      the PDF, so the label says that instead of "Download Report".

      It sits below the proof and above the form, so the page still ends on one
      form and one `id="contact"`.
    */
    {
      kind: 'cta',
      tone: 'card',
      heading:
        'Unlock success in the digital age. Grab your transformative guide now and #transformbetter!',
      ctas: [{ label: 'Explore the e-book', href: LINKS.transformBetterEbook }],
      photo: {
        src: '/photo/transform-better-ebook-covers.webp',
        alt: 'Four copies of the Transform Better e-book fanned out, the mountain road on the cover.',
      },
      photoHref: LINKS.transformBetterEbook,
    },
    // The dark "Not sure which one you need?" band was removed on 2026-08-30
    // ("bu siyah kutuya gerek yok"). Its button only scrolled to the contact
    // form that already closes this page, so nothing was lost with it.
    /*
      The page's own form, standing in for the shared closing band (see
      `NO_CONTACT_BAND` in `app/Layout.tsx`) rather than sitting beside it, so
      the document still ends on one form and one `id="contact"`.

      It is the band's form plus the "which solution?" boxes asked for on
      2026-08-31 — the three cards above, at least one of them — see
      `content/solutionsForm.ts`.
    */
    {
      kind: 'form',
      // The band's own heading, because this section stands in its place.
      heading: "Let's Get in Touch!",
      ...SOLUTIONS_FORM_EN,
    },
  ],
}
